import React from 'react'
import { useAnimation } from 'framer-motion'
import { useAnalytics } from 'reactfire'

import { EmotionFace } from './EmotionFace/EmotionFace'
import { CardsWrap, PullToBottom } from './styled'
import { QuestionCard } from './QuestionCard/QuestionCard'
import {
  animationVariants,
  allowedCodes,
  keyCodeToAnswer,
} from './QuestionCard/utils'

import { KeyboardHint } from 'src/components/QuizQuestions/KeyboardHint'
import { IQuiz, ActionAnswer } from 'src/api/quizzes/types'
import { QuestionAnswerPayload } from 'src/api/quizAnswers/types'
import { useMedia } from 'src/hooks/useMedia'
import { breakpoints } from 'src/styles/media'
import { analyticsEvents } from 'src/constants/analytics/events'
import { answerActionToAnalyticsAction } from 'src/constants/analytics/utils'

interface Props {
  quiz: IQuiz
  setProgress: (value: number) => void
  handleFinishedQuiz: (quizAnswers: QuestionAnswerPayload[]) => Promise<void>
  disableAnswering?: boolean
}

export const QuizQuestions: React.FC<Props> = ({
  quiz,
  handleFinishedQuiz,
  setProgress,
  disableAnswering = false,
}) => {
  const animation = useAnimation()
  const { logEvent } = useAnalytics()

  const [faceEmotion, setFaceEmotion] = React.useState<ActionAnswer>()
  const [answers, setAnswers] = React.useState<QuestionAnswerPayload[]>([])
  const [answerInProgress, setAnswerInProgress] = React.useState(false)
  const [currentQuestionId, setCurrentQuestionId] = React.useState<number>()

  const isDesktop = useMedia(breakpoints.desktop)

  React.useEffect(() => {
    if (answers.length === quiz.questions.length) {
      handleFinishedQuiz(answers)
    }
  }, [answers, answers.length, handleFinishedQuiz, quiz.questions.length])

  React.useEffect(() => {
    setCurrentQuestionId(quiz.questions[0].id)
  }, [quiz.questions, setCurrentQuestionId])

  React.useEffect(() => {
    const newProgress = ((answers.length + 1) / quiz.questions.length) * 100
    setProgress(newProgress)
  }, [answers.length, quiz.questions.length, setProgress])

  const setNextQuestionId = React.useCallback(() => {
    const currentQuestionArrayIndex = quiz.questions.findIndex(
      ({ id: questionId }) => questionId === currentQuestionId
    )
    if (currentQuestionArrayIndex >= 0) {
      const nextQuestion = quiz.questions[currentQuestionArrayIndex + 1]

      if (nextQuestion) {
        setCurrentQuestionId(nextQuestion.id)
      }
    }
  }, [currentQuestionId, quiz.questions])

  const answerQuestion = React.useCallback(
    async (answer: ActionAnswer) => {
      if (currentQuestionId && !answerInProgress && !disableAnswering) {
        setAnswerInProgress(true)
        setFaceEmotion(answer)
        await animation.start(animationVariants[answer])
        setFaceEmotion(undefined)
        setAnswers(answers.concat({ questionId: currentQuestionId, answer }))
        setNextQuestionId()
        setAnswerInProgress(false)

        const analyticsEvent = analyticsEvents.quizQuestionAnswered(
          answerActionToAnalyticsAction(answer)
        )
        logEvent(analyticsEvent.eventName, analyticsEvent.params)
      }
    },
    [
      animation,
      answerInProgress,
      answers,
      currentQuestionId,
      disableAnswering,
      logEvent,
      setNextQuestionId,
    ]
  )

  const handleKeyDown = React.useCallback(
    async ({ code }: KeyboardEvent) => {
      if (currentQuestionId) {
        if (allowedCodes.some((allowedKey) => allowedKey === code)) {
          const answer = keyCodeToAnswer(code)
          if (answer) {
            await answerQuestion(answer)
          }
        }
      }
    },
    [answerQuestion, currentQuestionId]
  )

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return (
    <>
      <CardsWrap>
        {[...quiz.questions].reverse().map((question) => (
          <QuestionCard
            key={question.id}
            animation={
              question.id === currentQuestionId ? animation : undefined
            }
            question={question}
            answerQuestion={answerQuestion}
            setFaceEmotion={setFaceEmotion}
            faceEmotion={faceEmotion}
            dragEnabled={!answerInProgress}
          />
        ))}
        <EmotionFace emotion={faceEmotion} />
      </CardsWrap>
      {isDesktop && (
        <PullToBottom>
          <KeyboardHint answerQuestion={answerQuestion} />
        </PullToBottom>
      )}
    </>
  )
}
